import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { KeyRound, ArrowLeft, Building2 } from 'lucide-react'
import { Input } from 'components/ui/Input'
import { Button } from 'components/ui/Button'
import { authApi } from 'lib/api'

export function ResetPassword() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const uid = useMemo(() => params.get('uid') || '', [params])
  const token = useMemo(() => params.get('token') || '', [params])
  const langParam = useMemo(() => params.get('lang') || '', [params])
  const [i18nReady, setI18nReady] = useState(() => {
    const target = (langParam || '').toLowerCase()
    const current = (i18n.language || '').toLowerCase()
    return !target || target === current
  })
  // Ensure page language matches link parameter (from email)
  useEffect(() => {
    const target = (langParam || '').toLowerCase()
    if (target && (target === 'en' || target === 'fr') && (i18n.language || '').toLowerCase() !== target) {
      setI18nReady(false)
      i18n.changeLanguage(target).finally(() => setI18nReady(true))
    }
  }, [langParam, i18n])
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')
    if (!uid || !token) {
      setError(t('auth.invalidResetLink', 'Invalid reset link'))
      return
    }
    if (password !== password2) {
      setError(t('auth.passwordsDoNotMatch', 'Passwords do not match'))
      return
    }
    setSubmitting(true)
    try {
      const res = await authApi.confirmPasswordReset({ uid, token, new_password: password })
      setMessage(res.data.message || t('auth.passwordResetSuccess', 'Password has been reset'))
      setTimeout(() => navigate('/login'), 1500)
    } catch (e: any) {
      setError(e?.response?.data?.error || t('auth.resetFailed', 'Failed to reset password'))
    } finally {
      setSubmitting(false)
    }
  }

  if (!i18nReady) {
    return <div className="min-h-screen flex items-center justify-center p-6 bg-muted" />
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted relative">
      {/* Subtle background gradient */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5" />
      {/* Soft grid pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="reset-grid" width="28" height="28" patternUnits="userSpaceOnUse">
              <path d="M 28 0 L 0 0 0 28" fill="none" stroke="currentColor" className="text-foreground" strokeWidth="0.6" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#reset-grid)" />
        </svg>
      </div>
      <div className="w-full max-w-md relative">
        {/* Brand header */}
        <div className="mb-6 flex items-center justify-center gap-2 text-foreground">
          <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
            <Building2 className="h-5 w-5" />
          </div>
          <span className="text-lg font-semibold tracking-tight">Selektia</span>
        </div>

        <div className={`bg-background border rounded-2xl p-6 sm:p-8 shadow-sm transition-all duration-300`}>
          <div className="flex items-center justify-center mb-6">
            <div className="mx-auto h-14 w-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <KeyRound className="h-7 w-7" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">{t('auth.resetPassword')}</h1>
          <p className="text-sm text-muted-foreground text-center">{t('auth.resetPasswordSubtitle')}</p>

          {message && (
            <div className="mt-6 animate-in slide-in-from-top-4 duration-300">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                <span className="inline-block h-5 w-5 rounded-full bg-emerald-600" />
                <p className="text-sm font-medium">{message}</p>
              </div>
            </div>
          )}
          {error && (
            <div className="mt-6 animate-in slide-in-from-top-4 duration-300">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                <span className="inline-block h-5 w-5 rounded-full bg-red-600" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5 mt-8">
            <div className="h-px bg-border" />
              <Input
              type="password"
              placeholder={t('auth.newPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-12"
            />
            <Input
              type="password"
              placeholder={t('auth.confirmPassword')}
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              required
              className="h-12"
            />
            <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={submitting}>
              {submitting ? t('common.saving') : t('auth.resetPassword')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 group">
              <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>

        {/* Page footer */}
        <div className="mt-6 text-center text-xs text-muted-foreground">
          {t('auth.needHelp')}{' '}
          <a href="mailto:support@selektia.io" className="underline hover:text-foreground">
            {t('auth.contactSupport')}
          </a>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword


